import { useEffect, useState } from 'react';
import { Alert, Col, Row, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { balanceLevel, useRefreshBalanceMutation, useSmsOverviewQuery } from './hooks';
import { BalanceCard } from './BalanceCard';
import { SpendTable } from './SpendTable';
import { SmsSettingsCard } from './SmsSettingsCard';
import { ThresholdModal } from './ThresholdModal';

const { Title, Text } = Typography;

// SMS-центр — главный дашборд владельца платформы (M5.4). Баланс SMSC + KPI +
// расход по оптикам. Баннер сверху при глобальной блокировке / приближении к
// порогу. Прототипный контрол «Симуляция баланса» — демо, в прод не переносим.
export function SmsCenterPage(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error, isFetching } = useSmsOverviewQuery();
  const refreshBalance = useRefreshBalanceMutation();
  const { mutate: refreshBalanceMutate } = refreshBalance;
  const [thresholdOpen, setThresholdOpen] = useState(false);

  // Автоопрос баланса при заходе на страницу — один раз на mount (mutate
  // стабилен), НЕ на каждый рендер/фокус: SMSC balance-API не стоит дёргать
  // без нужды. Дальше — вручную кнопкой «Обновить баланс».
  useEffect(() => {
    refreshBalanceMutate();
  }, [refreshBalanceMutate]);

  // Реальный опрос SMSC (не просто refetch кэша) — иначе баланс «нет данных»,
  // пока cron не отработал. mutation.onSuccess инвалидирует overview.
  const handleRefresh = (): void => {
    refreshBalance.mutate();
  };

  const level = data ? balanceLevel(data.balance) : 'ok';

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('platform.sms_center.title')}
      </Title>
      <Text type="secondary">{t('platform.sms_center.subtitle')}</Text>

      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      {data && level === 'blocked' && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          data-testid="banner-blocked"
          message={t('platform.banner.blocked_title')}
          description={t('platform.banner.blocked_description')}
        />
      )}
      {data && level === 'warn' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          data-testid="banner-warn"
          message={t('platform.banner.warn_title')}
        />
      )}

      {isLoading && <Skeleton active style={{ marginTop: 16 }} />}

      {data && (
        <Row gutter={[18, 18]} style={{ marginTop: 16 }} align="top">
          <Col xs={24} lg={9} xl={8}>
            <BalanceCard
              balance={data.balance}
              kpi={data.kpi}
              onConfigureThreshold={() => setThresholdOpen(true)}
              onRefresh={handleRefresh}
              refreshing={refreshBalance.isPending || isFetching}
            />
          </Col>
          <Col xs={24} lg={15} xl={16}>
            <SpendTable spend={data.spend} loading={isFetching} />
          </Col>
        </Row>
      )}

      <div style={{ marginTop: 18, maxWidth: 680 }}>
        <SmsSettingsCard />
      </div>

      <ThresholdModal
        open={thresholdOpen}
        currentThreshold={data?.balance.threshold_kzt ?? 0}
        onClose={() => setThresholdOpen(false)}
      />
    </div>
  );
}
